// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  Alert,
  AlertTitle,
  Card,
  Checkbox,
  Radio,
  Typography,
} from '@mui/material';
import { Label, OptionsSelector } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { classNames } from '@shared/ui-kit/utils';
import {
  NavigationControls,
  ScreenHeader,
  ScreenLayout,
} from '@shared/advisory/account-workflow/layout';
import { useWealthSourceFunds } from './use-wealth-source-funds';
import { Controller } from 'react-hook-form';

export const WealthSourceFunds = defineComponent({
  displayName: 'AccountOpeningWealthSourceFunds',
  useSetup: useWealthSourceFunds,
  View: ({
    t,
    form,
    onNext,
    isFormValid,
    estimatedAnnualIncomeOptions,
    sourceOfFundsOptions,
    mainIncomeSourceOptions,
    approximateWealthOptions,
    onToggleSourceOfFunds,
  }) => (
    <ScreenLayout
      actions={
        <NavigationControls
          form="wealth-source-funds-form"
          isFormValid={isFormValid}
        />
      }
    >
      <div className="w-full">
        <form id="wealth-source-funds-form" onSubmit={form.handleSubmit(onNext)}>
          <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
            <ScreenHeader
              title={
                <Translation
                  i18nKey="account-opening.screens.wealth-source-funds.title"
                  ns="components"
                />
              }
              subtitle={
                <Translation
                  i18nKey="account-opening.screens.wealth-source-funds.subtitle"
                  ns="components"
                />
              }
            />

            <Alert
              severity="info"
              className="!rounded-lg !bg-alert-info-bg text-alert-info-text"
              data-testid="wealth-source-funds-alert"
            >
              <AlertTitle>
                <Translation
                  i18nKey="account-opening.wealth-source-funds.alert.title"
                  ns="components"
                />
              </AlertTitle>

              <p>
                <Translation
                  i18nKey="account-opening.wealth-source-funds.alert.description"
                  ns="components"
                />
              </p>
            </Alert>

            <Card className="!rounded-xl !p-6" variant="outlined">
              <div className="flex flex-col gap-6">
                <Typography
                  variant="h5"
                  className="!text-h5 !font-semibold !text-title-navy"
                  data-testid="income-and-wealth-title"
                >
                  <Translation
                    i18nKey="account-opening.wealth-source-funds.income-and-wealth.title"
                    ns="components"
                  />
                </Typography>

                <fieldset className="col-span-1 m-0 grid min-w-0 gap-3 border-0 p-0">
                  <Label
                    htmlFor="main-income-source-selector"
                    className="!mb-0 block w-full"
                    required
                  >
                    <Translation
                      i18nKey="account-opening.wealth-source-funds.income-and-wealth.main-income-source.label"
                      ns="components"
                    />
                  </Label>

                  <Typography
                    variant="body2"
                    className="!text-sm !font-normal !text-subtitle"
                    data-testid="main-income-source-hint"
                  >
                    <Translation
                      i18nKey="account-opening.wealth-source-funds.income-and-wealth.main-income-source.hint"
                      ns="components"
                    />
                  </Typography>

                  <Controller
                    name="mainIncomeSource"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <OptionsSelector
                        optionsAvailable={mainIncomeSourceOptions}
                        onChangeValueSelected={field.onChange}
                        optionSelected={field.value}
                        defaultText={t(
                          'account-opening.wealth-source-funds.income-and-wealth.main-income-source.placeholder',
                          { ns: 'components' },
                        )}
                        selectType="white"
                        dataTestId="main-income-source-selector"
                        error={!!fieldState.error}
                        required
                      />
                    )}
                  />
                </fieldset>

                <fieldset className="col-span-1 m-0 grid min-w-0 gap-2 border-0 p-0 [&_legend]:float-none">
                  <Label component="legend" className="mb-2 block w-full" required>
                    <Translation
                      i18nKey="account-opening.wealth-source-funds.income-and-wealth.estimated-annual-income.label"
                      ns="components"
                    />
                  </Label>

                  <Typography
                    variant="body2"
                    className="mb-2 !text-sm !font-normal !text-subtitle"
                    data-testid="estimated-annual-income-hint"
                  >
                    <Translation
                      i18nKey="account-opening.wealth-source-funds.income-and-wealth.estimated-annual-income.hint"
                      ns="components"
                    />
                  </Typography>

                  <Controller
                    name="estimatedAnnualIncome"
                    control={form.control}
                    render={({ field }) => (
                      <div className="flex flex-col gap-3">
                        {estimatedAnnualIncomeOptions.map(option => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => field.onChange(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`estimated-annual-income-option-${option.value}`}
                            >
                              <Radio
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                              />

                              <Typography
                                variant="body1"
                                className="!text-sm !font-normal !text-title-navy sm:!text-base"
                              >
                                {option.label}
                              </Typography>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  />
                </fieldset>

                <fieldset className="col-span-1 m-0 grid min-w-0 gap-3 border-0 p-0">
                  <Label
                    htmlFor="approximate-wealth-selector"
                    className="!mb-0 block w-full"
                    required
                  >
                    <Translation
                      i18nKey="account-opening.wealth-source-funds.income-and-wealth.approximate-wealth.label"
                      ns="components"
                    />
                  </Label>

                  <Typography
                    variant="body2"
                    className="!text-sm !font-normal !text-subtitle"
                    data-testid="approximate-wealth-hint"
                  >
                    <Translation
                      i18nKey="account-opening.wealth-source-funds.income-and-wealth.approximate-wealth.hint"
                      ns="components"
                    />
                  </Typography>

                  <Controller
                    name="approximateWealth"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <OptionsSelector
                        optionsAvailable={approximateWealthOptions}
                        onChangeValueSelected={field.onChange}
                        optionSelected={field.value}
                        defaultText={t(
                          'account-opening.wealth-source-funds.income-and-wealth.approximate-wealth.placeholder',
                          { ns: 'components' },
                        )}
                        selectType="white"
                        dataTestId="approximate-wealth-selector"
                        error={!!fieldState.error}
                        required
                      />
                    )}
                  />
                </fieldset>
              </div>
            </Card>

            <Card className="!rounded-xl !p-6" variant="outlined">
              <div className="flex flex-col gap-6">
                <Typography
                  variant="h5"
                  className="!text-h5 !font-semibold !text-title-navy"
                  data-testid="source-of-funds-title"
                >
                  <Translation
                    i18nKey="account-opening.wealth-source-funds.source-of-funds.title"
                    ns="components"
                  />
                </Typography>

                <fieldset className="col-span-1 grid gap-2">
                  <Label
                    component="legend"
                    required
                    className="mb-4 !text-base !font-normal !text-subtitle"
                  >
                    <Translation
                      i18nKey="account-opening.wealth-source-funds.source-of-funds.description"
                      ns="components"
                    />
                  </Label>

                  <Controller
                    name="sourceOfFunds"
                    control={form.control}
                    render={({ field }) => (
                      <div className="flex flex-col gap-3">
                        {sourceOfFundsOptions.map(option => {
                          const isSelected = field.value.includes(option.value);

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => onToggleSourceOfFunds(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`source-of-funds-option-${option.value}`}
                            >
                              <Checkbox
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                                onClick={event => event.stopPropagation()}
                                onChange={() => onToggleSourceOfFunds(option.value)}
                              />

                              <Typography
                                variant="body1"
                                className="!text-sm !font-normal !text-title-navy sm:!text-base"
                              >
                                {option.label}
                              </Typography>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  />
                </fieldset>
              </div>
            </Card>
          </fieldset>
        </form>
      </div>
    </ScreenLayout>
  ),
});
